import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BuildingOffice2Icon, CpuChipIcon, LightBulbIcon, CodeBracketIcon, AcademicCapIcon, BoltIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import robotImage from '../img/robot-8530783_1280.jpg';
import dustVideo from '../img/Free_Dust_Impact_Video_source_287.mov';
import aiImage from '../img/aii.png';

// InfoCard for Figma Dark Theme
const InfoCard = ({ title, description, linkTo, linkText, imageUrl, category, icon: Icon }) => (
  <div className="bg-figma-dark-bg-secondary border border-figma-dark-border rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group hover:animate-subtle-3d-tilt-hover flex flex-col">
    {imageUrl && (
      <div className="w-full h-48 bg-figma-dark-bg-tertiary overflow-hidden">
        <img src={imageUrl} alt={title || 'Highlight image'} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
      </div>
    )}
    <div className="p-6 flex flex-col flex-grow">
      {Icon && !imageUrl && <Icon className="h-8 w-8 text-figma-dark-primary mb-4" />} 
      {category && <p className="text-xs text-figma-dark-accent uppercase tracking-wider mb-1 font-semibold">{category}</p>}
      <h3 className="text-lg font-semibold text-figma-dark-text-primary mb-2 group-hover:text-figma-dark-primary transition-colors">{title}</h3>
      {description && <p className="text-sm text-figma-dark-text-secondary leading-relaxed mb-4 flex-grow">{description}</p>}
      {linkTo && linkText && (
        <Link 
          to={linkTo}
          className="text-sm font-semibold text-figma-dark-primary hover:text-figma-dark-primary-hover inline-flex items-center mt-auto group-hover:underline"
        >
          {linkText} <ArrowRightIcon className="ml-1.5 h-4 w-4" />
        </Link>
      )}
    </div>
  </div>
);

const HomePage = () => {
  return (
    <div className="min-h-screen bg-scale-bg-primary text-scale-text-primary">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${robotImage})`,
            filter: 'brightness(0.5)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-scale-bg-primary/70 to-scale-bg-secondary/70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.15),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.15),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-block mb-6 animate-fade-in-up">
              <span className="px-4 py-2 rounded-full bg-scale-bg-secondary/40 backdrop-blur-sm border border-scale-border text-scale-text-secondary text-sm">
                AI-Powered Solutions
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in-up">
              <span className="bg-gradient-to-r from-scale-primary to-scale-accent bg-clip-text text-transparent">
                Transform Your Business
              </span>
              <br />
              with AI-Powered Solutions
            </h1>
            <p className="text-xl md:text-2xl text-scale-text-secondary mb-8 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Unlock the power of artificial intelligence to drive innovation, efficiency, and growth in your organization.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full bg-gradient-to-r from-scale-primary to-scale-accent text-white font-medium hover:shadow-scale-glow transition-all duration-300 hover:scale-105"
              >
                Get Started
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 rounded-full border border-scale-border text-scale-text-primary hover:bg-scale-bg-secondary/40 backdrop-blur-sm transition-all duration-300"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-scale-bg-secondary relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.7)' }}
            onLoadedData={(e) => e.target.play()}
          >
            <source src={dustVideo} type="video/quicktime" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-scale-bg-primary/60 to-scale-bg-secondary/60"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.15),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.15),transparent_50%)]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="px-4 py-2 rounded-full bg-scale-bg-primary/40 backdrop-blur-sm border border-scale-border text-scale-text-secondary text-sm mb-4 inline-block">
              Industry Solutions
            </span>
            <h2 className="text-3xl md:text-4xl font-bold">
              Use Cases
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Autonomous Vehicles",
                description: "Train and validate AI models for self-driving cars with our comprehensive data platform.",
                icon: "🚗",
                gradient: "from-blue-500/20 to-purple-500/20"
              },
              {
                title: "E-commerce",
                description: "Enhance product search and recommendations with advanced AI algorithms.",
                icon: "🛍️",
                gradient: "from-green-500/20 to-teal-500/20"
              },
              {
                title: "Healthcare",
                description: "Improve medical imaging analysis and patient care with AI-powered solutions.",
                icon: "🏥",
                gradient: "from-red-500/20 to-pink-500/20"
              },
              {
                title: "Financial Services",
                description: "Detect fraud and optimize trading strategies using machine learning.",
                icon: "💳",
                gradient: "from-yellow-500/20 to-orange-500/20"
              },
              {
                title: "Manufacturing",
                description: "Implement predictive maintenance and quality control with AI vision systems.",
                icon: "🏭",
                gradient: "from-indigo-500/20 to-purple-500/20"
              },
              {
                title: "Retail",
                description: "Optimize inventory management and customer experience with AI insights.",
                icon: "🏪",
                gradient: "from-cyan-500/20 to-blue-500/20"
              }
            ].map((useCase, index) => (
              <div
                key={index}
                className="p-6 rounded-xl bg-scale-bg-primary/40 backdrop-blur-sm border border-scale-border hover:border-scale-primary transition-all duration-300 animate-fade-in-up group relative overflow-hidden"
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${useCase.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                <div className="relative z-10">
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">{useCase.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{useCase.title}</h3>
                  <p className="text-scale-text-secondary">{useCase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Showcase */}
      <section className="py-20 bg-scale-bg-primary relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${aiImage})`,
            filter: 'brightness(0.7)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-scale-bg-primary/60 to-scale-bg-secondary/60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(124,58,237,0.15),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(16,185,129,0.15),transparent_50%)]"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="px-4 py-2 rounded-full bg-scale-bg-secondary/40 backdrop-blur-sm border border-scale-border text-scale-text-secondary text-sm mb-4 inline-block">
                Our Technology
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                <span className="bg-gradient-to-r from-scale-primary to-scale-accent bg-clip-text text-transparent">
                  Advanced Technology
                </span>
              </h2>
              <p className="text-xl text-scale-text-secondary mb-8">
                Our platform combines state-of-the-art AI models with enterprise-grade infrastructure to deliver reliable and scalable solutions.
              </p>
              <div className="space-y-4">
                {[
                  "Deep Learning & Neural Networks",
                  "Computer Vision & Image Processing",
                  "Natural Language Processing",
                  "Reinforcement Learning",
                  "Edge Computing & IoT Integration"
                ].map((tech, index) => (
                  <div key={index} className="flex items-center space-x-3 group">
                    <div className="w-2 h-2 rounded-full bg-scale-primary group-hover:scale-150 transition-transform duration-300"></div>
                    <span className="text-scale-text-primary group-hover:text-scale-primary transition-colors duration-300">{tech}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="aspect-w-16 aspect-h-9 rounded-xl overflow-hidden bg-gradient-to-br from-scale-primary/20 to-scale-accent/20 p-1">
                <div className="w-full h-full bg-scale-bg-secondary/40 backdrop-blur-sm rounded-lg flex items-center justify-center relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-scale-primary/10 to-scale-accent/10 animate-pulse"></div>
                  <div className="relative z-10">
                    <div className="absolute -inset-4 bg-gradient-to-r from-scale-primary/20 to-scale-accent/20 blur-2xl"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Showcase */}
      <section className="py-20 bg-scale-bg-primary relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <span className="px-4 py-2 rounded-full bg-scale-bg-secondary/40 backdrop-blur-sm border border-scale-border text-scale-text-secondary text-sm mb-4 inline-block">
              Seamless Integration
            </span>
            <h2 className="text-3xl md:text-4xl font-bold">
              Works With Your Stack
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { name: "AWS", icon: "☁️", color: "from-orange-500/20 to-yellow-500/20" },
              { name: "Azure", icon: "🌐", color: "from-blue-500/20 to-cyan-500/20" },
              { name: "GCP", icon: "🔧", color: "from-red-500/20 to-orange-500/20" },
              { name: "Kubernetes", icon: "⚓", color: "from-blue-500/20 to-indigo-500/20" },
              { name: "TensorFlow", icon: "🧠", color: "from-orange-500/20 to-red-500/20" },
              { name: "PyTorch", icon: "🔥", color: "from-red-500/20 to-orange-500/20" },
              { name: "Docker", icon: "🐳", color: "from-blue-500/20 to-cyan-500/20" },
              { name: "GitHub", icon: "📦", color: "from-gray-500/20 to-gray-700/20" }
            ].map((integration, index) => (
              <div
                key={index}
                className="p-6 rounded-xl bg-scale-bg-primary/40 backdrop-blur-sm border border-scale-border hover:border-scale-primary transition-all duration-300 animate-fade-in-up group"
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${integration.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl`}></div>
                <div className="relative z-10 text-center">
                  <div className="text-4xl mb-2 group-hover:scale-110 transition-transform duration-300">{integration.icon}</div>
                  <h3 className="text-lg font-semibold">{integration.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Testimonials */}
      <section className="py-20 bg-scale-bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="px-4 py-2 rounded-full bg-scale-bg-secondary border border-scale-border text-scale-text-secondary text-sm mb-4 inline-block">
              Success Stories
            </span>
            <h2 className="text-3xl md:text-4xl font-bold">
              Trusted by Industry Leaders
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "YourCompany's AI platform has transformed our business operations, delivering unprecedented efficiency and insights.",
                author: "Sarah Johnson",
                role: "CTO",
                company: "TechCorp",
                avatar: "👩‍💼"
              },
              {
                quote: "The level of innovation and support we've received has been exceptional. A true game-changer for our industry.",
                author: "Michael Chen",
                role: "Head of AI",
                company: "InnovateAI",
                avatar: "👨‍💻"
              },
              {
                quote: "Implementing YourCompany's solutions has helped us stay ahead of the curve in an increasingly competitive market.",
                author: "Emily Rodriguez",
                role: "Director of Innovation",
                company: "FutureTech",
                avatar: "👩‍🔬"
              }
            ].map((testimonial, index) => (
              <div
                key={index}
                className="p-6 rounded-xl bg-scale-bg-secondary border border-scale-border hover:border-scale-primary transition-all duration-300 animate-fade-in-up group"
                style={{ animationDelay: `${0.2 * index}s` }}
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">{testimonial.avatar}</div>
                <p className="text-scale-text-secondary mb-6">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold text-scale-text-primary">{testimonial.author}</p>
                  <p className="text-sm text-scale-text-secondary">{testimonial.role}, {testimonial.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-scale-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="px-4 py-2 rounded-full bg-scale-bg-primary border border-scale-border text-scale-text-secondary text-sm mb-4 inline-block">
              Case Studies
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Real-World Success Stories
            </h2>
            <p className="text-xl text-scale-text-secondary max-w-3xl mx-auto">
              Discover how our solutions have helped organizations across industries achieve their goals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Healthcare Case Study */}
            <div className="bg-scale-bg-primary rounded-xl overflow-hidden border border-scale-border hover:border-scale-primary transition-all duration-300 group">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="px-4 py-2 rounded-full bg-scale-primary/10 text-scale-primary text-sm font-medium">
                    Healthcare
                  </span>
                  <span className="text-scale-text-secondary">2023</span>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-scale-text-primary group-hover:text-scale-primary transition-colors">
                  AI-Powered Diagnostic System
                </h3>
                <p className="text-scale-text-secondary mb-6">
                  Implemented an advanced AI diagnostic system for a leading hospital network, resulting in 40% faster diagnosis times and 95% accuracy in preliminary assessments.
                </p>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">Reduced diagnosis time by 40%</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">95% accuracy in preliminary assessments</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">$2M annual cost savings</span>
                  </div>
                </div>
                <Link to="/case-studies/healthcare" className="text-scale-primary hover:text-scale-accent inline-flex items-center">
                  Read Full Case Study <ArrowRightIcon className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Manufacturing Case Study */}
            <div className="bg-scale-bg-primary rounded-xl overflow-hidden border border-scale-border hover:border-scale-primary transition-all duration-300 group">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="px-4 py-2 rounded-full bg-scale-accent/10 text-scale-accent text-sm font-medium">
                    Manufacturing
                  </span>
                  <span className="text-scale-text-secondary">2023</span>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-scale-text-primary group-hover:text-scale-primary transition-colors">
                  Smart Factory Automation
                </h3>
                <p className="text-scale-text-secondary mb-6">
                  Deployed a comprehensive IoT and AI solution for a global manufacturing company, achieving 60% reduction in downtime and 35% increase in production efficiency.
                </p>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">60% reduction in downtime</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">35% increase in production efficiency</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">ROI achieved within 8 months</span>
                  </div>
                </div>
                <Link to="/case-studies/manufacturing" className="text-scale-primary hover:text-scale-accent inline-flex items-center">
                  Read Full Case Study <ArrowRightIcon className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Financial Services Case Study */}
            <div className="bg-scale-bg-primary rounded-xl overflow-hidden border border-scale-border hover:border-scale-primary transition-all duration-300 group">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="px-4 py-2 rounded-full bg-scale-primary/10 text-scale-primary text-sm font-medium">
                    Financial Services
                  </span>
                  <span className="text-scale-text-secondary">2023</span>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-scale-text-primary group-hover:text-scale-primary transition-colors">
                  AI-Powered Risk Assessment
                </h3>
                <p className="text-scale-text-secondary mb-6">
                  Developed an advanced risk assessment system for a leading financial institution, reducing fraud losses by 75% and improving customer satisfaction scores.
                </p>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">75% reduction in fraud losses</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">90% faster risk assessment</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">40% improvement in customer satisfaction</span>
                  </div>
                </div>
                <Link to="/case-studies/financial" className="text-scale-primary hover:text-scale-accent inline-flex items-center">
                  Read Full Case Study <ArrowRightIcon className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Retail Case Study */}
            <div className="bg-scale-bg-primary rounded-xl overflow-hidden border border-scale-border hover:border-scale-primary transition-all duration-300 group">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <span className="px-4 py-2 rounded-full bg-scale-accent/10 text-scale-accent text-sm font-medium">
                    Retail
                  </span>
                  <span className="text-scale-text-secondary">2023</span>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-scale-text-primary group-hover:text-scale-primary transition-colors">
                  Smart Inventory Management
                </h3>
                <p className="text-scale-text-secondary mb-6">
                  Implemented an AI-driven inventory management system for a retail chain, optimizing stock levels and reducing waste by 45%.
                </p>
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">45% reduction in waste</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">30% improvement in stock turnover</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-scale-primary"></div>
                    <span className="text-scale-text-secondary">$1.5M annual savings</span>
                  </div>
                </div>
                <Link to="/case-studies/retail" className="text-scale-primary hover:text-scale-accent inline-flex items-center">
                  Read Full Case Study <ArrowRightIcon className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/case-studies"
              className="inline-flex items-center px-8 py-4 rounded-full border border-scale-border text-scale-text-primary hover:bg-scale-bg-primary/40 backdrop-blur-sm transition-all duration-300"
            >
              View All Case Studies <ArrowRightIcon className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-scale-bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="px-4 py-2 rounded-full bg-scale-bg-primary border border-scale-border text-scale-text-secondary text-sm mb-4 inline-block">
              Get Started Today
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-scale-text-secondary mb-8 max-w-2xl mx-auto">
              Join thousands of companies already using our platform to transform their business.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="inline-block px-8 py-4 rounded-full bg-gradient-to-r from-scale-primary to-scale-accent text-white font-medium hover:shadow-scale-glow transition-all duration-300 hover:scale-105"
              >
                Start Your Journey
              </Link>
              <Link
                to="/solutions"
                className="inline-block px-8 py-4 rounded-full border border-scale-border text-scale-text-primary hover:bg-scale-bg-primary/40 backdrop-blur-sm transition-all duration-300"
              >
                Explore Solutions
              </Link>
              <Link
                to="/case-studies"
                className="inline-block px-8 py-4 rounded-full border border-scale-border text-scale-text-primary hover:bg-scale-bg-primary/40 backdrop-blur-sm transition-all duration-300"
              >
                View Case Studies
              </Link>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4 text-scale-text-secondary">
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-scale-primary mr-2" />
                <span>Free Consultation</span>
              </div>
              <div className="hidden sm:block">•</div>
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-scale-primary mr-2" />
                <span>No Credit Card Required</span>
              </div>
              <div className="hidden sm:block">•</div>
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-scale-primary mr-2" />
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 